import type { MomentDeLaNuit } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * La rue, à un instant donné de la nuit.
 *
 * Un seul SVG dont quelques attributs changent d'une heure à l'autre. Rien n'est
 * redessiné image par image : les transitions sont laissées au navigateur, ce
 * qui garde l'animation fluide même sur une tablette d'entrée de gamme.
 */
export function RueDeNuit({ moment }: { moment: MomentDeLaNuit }) {
  const intensite = moment.niveau / 100;
  const cielNuit = moment.nuit;

  return (
    <svg
      viewBox="0 0 400 200"
      className="h-auto w-full rounded-2xl"
      role="img"
      aria-label={`${T.comprendre.heure} ${moment.heure} — ${
        moment.niveau > 0 ? T.comprendre.lampeAllumee : T.comprendre.lampeEteinte
      }, ${moment.passants} ${T.comprendre.passants}`}
    >
      <rect
        width="400"
        height="200"
        fill={cielNuit ? '#0F172A' : '#BAE6FD'}
        className="transition-[fill] duration-[400ms] ease-linear"
      />

      {cielNuit && (
        <g aria-hidden="true" opacity={1 - intensite * 0.6}>
          <circle cx="60" cy="40" r="1.6" fill="#E2E8F0" />
          <circle cx="140" cy="28" r="1.2" fill="#E2E8F0" />
          <circle cx="300" cy="46" r="1.6" fill="#E2E8F0" />
          <circle cx="350" cy="24" r="1.2" fill="#E2E8F0" />
        </g>
      )}

      {/* La chaussée */}
      <rect y="150" width="400" height="50" fill={cielNuit ? '#1E293B' : '#94A3B8'} />

      {/* Le halo, qui suit l'intensité du lampadaire */}
      <ellipse
        cx="200"
        cy="165"
        rx="95"
        ry="34"
        fill="#FCD34D"
        opacity={intensite * 0.55}
        className="transition-[opacity] duration-[400ms] ease-linear"
      />

      {/* Le lampadaire */}
      <g aria-hidden="true">
        <rect x="197" y="70" width="6" height="85" fill="#64748B" />
        <circle
          cx="200"
          cy="66"
          r="10"
          fill={moment.niveau > 0 ? '#FBBF24' : '#475569'}
          opacity={moment.niveau > 0 ? 0.35 + intensite * 0.65 : 1}
          className="transition-[fill,opacity] duration-[400ms] ease-linear"
        />
      </g>

      {/* Les passants : des silhouettes simples, sans visage ni identité */}
      <g aria-hidden="true">
        {Array.from({ length: Math.min(moment.passants, 6) }, (_, index) => (
          <g key={index} transform={`translate(${70 + index * 45}, 0)`}>
            <circle cx="0" cy="138" r="5" fill={cielNuit ? '#CBD5E1' : '#334155'} />
            <rect
              x="-4"
              y="144"
              width="8"
              height="16"
              rx="3"
              fill={cielNuit ? '#CBD5E1' : '#334155'}
            />
          </g>
        ))}
      </g>

      <text
        x="12"
        y="26"
        className="fill-current text-[15px] font-bold"
        fill={cielNuit ? '#F1F5F9' : '#0F172A'}
      >
        {String(moment.heure).padStart(2, '0')}:00
      </text>

      {moment.passants === 0 && (
        <text
          x="388"
          y="26"
          textAnchor="end"
          className="text-[13px] font-semibold"
          fill={cielNuit ? '#94A3B8' : '#334155'}
        >
          {T.comprendre.rueVide}
        </text>
      )}
    </svg>
  );
}
